import * as React from "react"

/**
 * Единый SEO-компонент для `export const Head`.
 * Собирает title, description, canonical, Open Graph и Twitter Card из одного места
 * с разумными дефолтами — чтобы на страницах не забывались canonical/og/twitter.
 *
 * Использование:
 *   export const Head = () => (
 *     <Seo title="…" description="…" path="/clients/" image="https://…">
 *       <Breadcrumbs list={…} />   // доп. теги — как children
 *       <Schema items={…} />
 *     </Seo>
 *   )
 *
 * Можно передать либо `path` (относительный, дополнится доменом), либо `url` (полный).
 */

const SITE_URL = "https://www.carrotquest.io"
const DEFAULT_OG_IMAGE = `${SITE_URL}/cover/ru/index.png`

type SeoProps = {
  /** Заголовок вкладки и og:title / twitter:title */
  title: string
  /** meta description + og/twitter description */
  description?: string
  /** Относительный путь страницы, напр. "/clients/". Дополняется доменом для canonical/og:url */
  path?: string
  /** Полный canonical-URL. Приоритетнее path, если задан */
  url?: string
  /** Абсолютный URL картинки для соцсетей. По умолчанию — обложка сайта */
  image?: string
  /** Закрыть страницу от индексации. По умолчанию вместе с `nofollow`. */
  noindex?: boolean
  /**
   * Разрешить обход ссылок на закрытой от индексации странице:
   * `noindex, follow` вместо `noindex, nofollow`. Нужно, когда страницу
   * не индексируем, но хотим, чтобы вес по ссылкам с неё продолжал ходить.
   */
  follow?: boolean
  /** og:type, по умолчанию "website" (для статей можно "article") */
  ogType?: string
  /** Дополнительные head-теги: Breadcrumbs, Schema, hreflang и т.п. */
  children?: React.ReactNode
}

const Seo: React.FC<SeoProps> = ({ title, description, path, url, image, noindex = false, follow = false, ogType = "website", children }) => {
  const canonical = url || (path ? `${SITE_URL}${path}` : undefined)
  const ogImage = image || DEFAULT_OG_IMAGE

  return (
    <>
      <title>{title}</title>
      {description && <meta name="description" content={description} />}
      {canonical && <link rel="canonical" href={canonical} />}
      {noindex && <meta name="robots" content={follow ? "noindex, follow" : "noindex, nofollow"} />}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="Carrot quest" />
      <meta property="og:title" content={title} />
      {description && <meta property="og:description" content={description} />}
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:image" content={ogImage} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />

      {children}
    </>
  )
}

export default Seo
